import { array, concat, fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BlockRenderStep } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import eq from 'webapp/helpers/eq';
import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import exposeDatapoint from 'webapp/modifiers/expose-datapoint';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockContentCommonComparesComponent from 'webapp/components/block/content/common/compares';
import BlockContentCommonValueComponent from 'webapp/components/block/content/common/value';
import BlockContentTableCompareLabelComponent from 'webapp/components/block/content/table/compare-label';
import BlockContentTableMediaLabelComponent from 'webapp/components/block/content/table/media-label';
import type { LazyLoadItem } from 'webapp/components/common/list/lazy-load';
import CommonListLoadNextComponent from 'webapp/components/common/list/load-next';

import type BlockRenderer from 'webapp/models-custom/block-renderer';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type BlockState from 'webapp/models-custom/block-state';
import type {
	AggregationCell,
	TableStructureCell,
	TableStructureRow,
} from 'webapp/models-custom/visualization/table';
import type VisualizationTable from 'webapp/models-custom/visualization/table';

const getLabelCellText = (row: { label: TableStructureRow['label'] }, i: number): string => {
	return row.label.parts[i]?.value ?? '';
};

interface Args {
	blockState: BlockState;
	blockRenderer: BlockRenderer;
	stickyHeaderColor: string;
	lazyloadRows: boolean;
}

interface TableStructureRowWithAggregationCells extends Omit<TableStructureRow, 'cells'> {
	cells: (TableStructureCell | AggregationCell)[];
}

export default class BlockContentTableComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.itemsShown = Math.min(this.initialItemsShown, this.allRows.length);
	}

	get block() {
		return this.args.blockState.block;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization(): VisualizationTable {
		return this.blockSetup.visualization as VisualizationTable;
	}

	get showTable() {
		return !!this.visualization.tableStructure?.rows.length;
	}

	get showRowNumbers() {
		return this.block.hasRowNumbers;
	}

	get labelColumnHeaders() {
		return this.visualization.labelColumnHeaders;
	}

	get tableStructure() {
		return this.visualization.tableStructure;
	}

	get showAggregationAsRow() {
		return this.visualization.showAggregationAsRow;
	}

	get showAggregationAsColumn() {
		return this.visualization.showAggregationAsColumn;
	}

	get aggregationData() {
		return this.visualization.aggregationData;
	}

	get mediaEnabled() {
		return this.block.mediaEnabled;
	}

	get labelColumnCount() {
		return this.visualization.labelColumnCount;
	}

	get labelColumnIndices(): number[] {
		return Array.from({ length: this.labelColumnCount }, (_, i) => i);
	}

	@cached
	get allRows() {
		const rows = Array.from(
			this.tableStructure?.rows || [],
		) as TableStructureRowWithAggregationCells[];
		if (this.showAggregationAsColumn) {
			const aggregationData = this.aggregationData;
			rows.forEach((row, idx) => {
				const cell = aggregationData.cells[idx];
				if (cell) {
					row.cells.push(cell);
				}
			});
		}
		return rows;
	}

	get allRowsForLoadNext() {
		// to shut up the glinter
		return this.allRows as LazyLoadItem[];
	}

	get columnHeaders() {
		const headers = Array.from(this.tableStructure?.columnHeaders || []),
			aggregationData = this.aggregationData;
		if (this.showAggregationAsColumn) {
			headers.push({
				index: headers.length,
				label: aggregationData.label,
				compareTitle: '',
			});
		}
		return headers;
	}

	// ----- Lazyloading ------

	private loadNext: (() => void) | null = null;

	initialItemsShown = 30;
	nextItemsShown = 20;
	private bottomDistance = 200;

	@tracked itemsShown: number;

	get visibleRows() {
		if (this.args.lazyloadRows) {
			return this.allRows.slice(0, this.itemsShown);
		}
		return this.allRows;
	}

	get areAllItemsLoaded() {
		if (this.args.lazyloadRows) {
			return this.allRows.length <= this.itemsShown;
		}
		return true;
	}

	@action
	initializeLoadNext(loadNext: () => void, node: HTMLElement) {
		if (!this.args.lazyloadRows) {
			return;
		}
		const targetNode = node.closest<HTMLElement>('.block')!;
		if (targetNode) {
			this.loadNext = loadNext || null;
			targetNode.addEventListener('scroll', this.debounceAutoLoad.bind(this, targetNode));
			this.autoLoad(targetNode); // to fix if the initial is less than space on screen
		}
	}

	@action
	debounceAutoLoad(blockNode: HTMLElement) {
		debounce(this, this.autoLoad, blockNode, 200);
	}

	@action
	autoLoad(blockNode: HTMLElement): void {
		const scrollTop = blockNode.scrollTop,
			scrollHeight = blockNode.scrollHeight,
			containerHeight = blockNode.offsetHeight;

		const isBottomThresholdVisible =
			scrollTop + containerHeight >= scrollHeight - this.bottomDistance;

		if (isBottomThresholdVisible && !this.areAllItemsLoaded) {
			this.loadNext?.();

			setTimeout(() => {
				this.autoLoad(blockNode);
			}, 200); // Allow time for newly appended items to load in UI, so isBottomThresholdVisible isn't falsely true
		}
	}

	@action
	updateVisibleItems(newVisibleItems: unknown[]) {
		if (newVisibleItems.length !== this.itemsShown) {
			this.itemsShown = newVisibleItems.length;
			schedule('afterRender', this, () => {
				this.updateVisibleColumns();
			});
		}
	}

	// ------------- shown columns logic START ---------------

	tableNode: HTMLElement | null = null;

	@tracked private firstVisibleColumnIndex = 0;
	@tracked private lastVisibleColumnIndex = 0;

	get lastColumnIndex() {
		return this.columnHeaders.length - 1;
	}

	get showNext() {
		return this.lastVisibleColumnIndex < this.lastColumnIndex;
	}

	get showPrev() {
		return this.firstVisibleColumnIndex > 0;
	}

	@action
	goNext() {
		if (!this.showNext) {
			return;
		}
		this.firstVisibleColumnIndex++;
		this.updateVisibleColumns();
	}

	@action
	goPrev() {
		if (!this.showPrev) {
			return;
		}
		this.firstVisibleColumnIndex--;
		this.updateVisibleColumns();
	}

	private updateVisibleColumns() {
		if (!isAlive(this)) {
			return;
		}
		// all next columns are visible (state before we calculate column widths)
		this.lastVisibleColumnIndex = this.lastColumnIndex;
		this.showAndHideElements();

		this.preColumnUpdate();
		this.updateFirstAndLastVisibleColumns();
		this.postColumnUpdate();
	}

	private preColumnUpdate() {
		const node = this.tableNode;
		if (!node) {
			return;
		}
		node.style.height = `${node.offsetHeight}px`;
		node.style.display = 'block';
		node.style.overflow = 'hidden';
		node.style.opacity = '0';
	}

	private updateFirstAndLastVisibleColumns() {
		const node = this.tableNode;
		if (!node) {
			return;
		}

		let availableWidth = Math.floor(this.args.blockRenderer.blockInnerWidth);
		const sizePerEm = this.args.blockRenderer.blockFontSize;
		// leave 30% of the block, but between 8 and 16ems — per label column,
		// so split row labels reserve enough for every part to stay visible
		const spaceForOneLabelColumn = Math.ceil(
			Math.max(Math.min(availableWidth * 0.3, 16 * sizePerEm), 8 * sizePerEm),
		);
		const spaceForLabelColumns = spaceForOneLabelColumn * this.labelColumnCount;

		// measure header widths
		const ths = node.querySelectorAll<HTMLElement>('thead .hideable');
		const thWidths: number[] = [];

		availableWidth -= spaceForLabelColumns;
		let lastVisibleColumnIndex = this.firstVisibleColumnIndex;
		Array.from(ths).some((th: HTMLElement) => {
			const idx = +(th.dataset.colidx || '');
			if (idx < lastVisibleColumnIndex) {
				return false;
			}
			const width = th.offsetWidth;

			// when we have compares, they are seperate columns but have the same idx
			// if the width has already been set, it means this is the "second pass" for compare
			const isCompare = typeof thWidths[idx] === 'number';

			if (!thWidths[idx]) {
				thWidths[idx] = 0;
			}
			thWidths[idx] += width;
			availableWidth -= width;
			if (availableWidth > 0 || thWidths.length === 1) {
				// we need to show at least one values+compares column
				lastVisibleColumnIndex = idx;
			} else {
				if (isCompare) {
					// means that the number (previous pass) fit, but compare (this pass) did not
					// which means we may not show the number either
					lastVisibleColumnIndex--;
				}
				return true;
			}
			return false;
		});

		this.lastVisibleColumnIndex = lastVisibleColumnIndex;
		this.showAndHideElements();
	}

	private showAndHideElements() {
		const node = this.tableNode;
		if (node) {
			const shownClasses: string[] = [];
			for (let i = this.firstVisibleColumnIndex; i <= this.lastVisibleColumnIndex; i++) {
				shownClasses.push(`col--${i}`);
			}
			const allColsThsAndTds = node.querySelectorAll('.hideable');
			allColsThsAndTds.forEach((el) => {
				const classList = el.classList;
				if (shownClasses.some((className) => classList.contains(className))) {
					el.classList.remove('hidden');
				} else {
					el.classList.add('hidden');
				}
			});
		}
	}

	private postColumnUpdate() {
		const node = this.tableNode;
		if (!node) {
			return;
		}
		node.removeAttribute('style');
	}

	// ------------- shown columns logic END ---------------

	// ------------ sticky header logic START --------------

	private stickyHeaderObserver: IntersectionObserver | null = null;

	@action
	changeStickyHeaderBackground(node: HTMLElement): void {
		node.style.background = this.args.stickyHeaderColor;
	}

	private setupObserverForStickyHeader(node: HTMLElement): void {
		if (typeof window.IntersectionObserver === 'undefined') {
			return;
		}

		if (!node) {
			// thead
			return;
		}

		const blockNode = this.args.blockRenderer.node?.querySelector<HTMLElement>('.block'),
			blockContentNode = node.closest<HTMLElement>('.block-content'),
			blockTopNode = blockContentNode?.querySelector<HTMLElement>('.block-content__top'),
			titleNode = blockContentNode?.querySelector<HTMLElement>('.bc__title');

		this.stickyHeaderObserver = new IntersectionObserver(
			([e]) => {
				const stickyClass = 'sticky-header--stuck',
					isStickyHeaderStuck = e && e.intersectionRatio < 1;

				// special check for to prevent coloring header while clicking on next or prev arrow btn
				// problem - when we were switching columns (next/prev btn) thead was visible and header became sticky, because
				// intersection observer was triggered (thead was in viewport).
				let topElementsHeight = 0;
				if (blockTopNode) {
					topElementsHeight += blockTopNode.offsetHeight;
				}
				if (titleNode) {
					topElementsHeight += titleNode.offsetHeight;
				}
				if (blockNode) {
					topElementsHeight += parseFloat(getComputedStyle(blockNode).paddingTop);
				}

				if ((blockNode?.scrollTop || 0) <= topElementsHeight) {
					node.classList.remove(stickyClass); // cleanup
					return;
				}

				node.classList.toggle(stickyClass, isStickyHeaderStuck);
				this.changeStickyHeaderBackground(node);
			},
			{
				root: blockNode,
				threshold: [0, 0.25, 0.5, 0.75, 1],
			},
		);

		this.stickyHeaderObserver.observe(node);
	}

	@action
	setupStickyHeader(node: HTMLElement): void {
		this.setupObserverForStickyHeader(node);
	}

	@action
	teardownStickyHeader(node: HTMLElement): void {
		if (this.stickyHeaderObserver) {
			this.stickyHeaderObserver.unobserve(node);
			this.stickyHeaderObserver = null;
		}
	}

	// ------------- sticky header logic END ---------------

	private teardownFns: (() => void)[] = [];

	@action
	setupView(node: HTMLElement): void {
		this.tableNode = node.querySelector('table');
		const blockRenderer = this.args.blockRenderer;
		blockRenderer.registerStepHandler(BlockRenderStep.CONTENT, this, () => {
			return new Promise((resolve) => {
				// next so there is time for things to rerender in DOM
				next(this, () => {
					this.updateVisibleColumns();
					resolve();
				});
			});
		});
		this.updateVisibleColumns();

		// prepare functions for teardown, since they dont have access to this.args
		this.teardownFns.push(() => {
			blockRenderer.unregisterStepHandler(BlockRenderStep.CONTENT, this);
		});
	}

	@action
	teardownView(): void {
		this.teardownFns.forEach((fn) => fn());
	}
	<template>
		<div
			class="bc__table {{unless this.showTable 'hidden'}}"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			{{! template-lint-disable table-groups }}
			<table width="100%" cellspacing="0" cellpadding="0">
				<colgroup>
					{{#if this.showRowNumbers}}
						<col width="1" />
						{{! minimum, but expand }}
					{{/if}}
					{{! flexible label column(s) }}
					{{#each this.labelColumnIndices}}
						<col />
					{{/each}}
					{{#each this.columnHeaders as |columnHeader idx|}}
						<col class="hideable col--{{idx}}" />
						{{#if columnHeader.compareTitle}}
							<col class="hideable col--{{idx}}" />
						{{/if}}
					{{/each}}
				</colgroup>
				<thead
					{{didInsert this.setupStickyHeader}}
					{{didUpdate this.changeStickyHeaderBackground @stickyHeaderColor}}
					{{willDestroy this.teardownStickyHeader}}
				>
					<tr class="bc__table__row--header">
						{{#if this.showRowNumbers}}
							<th class="bc__table__cell--row-index">
								<span class="bc__label">#</span>
							</th>
						{{/if}}
						{{#each this.labelColumnIndices as |i|}}
							<th class="bc__table__cell--label">
								<span class="bc__label">
									{{get this.labelColumnHeaders i}}
								</span>
							</th>
						{{/each}}
						{{#each this.columnHeaders as |columnHeader idx|}}
							<th
								class="bc__table__cell--value hideable col--{{idx}} action-target"
								data-colidx={{idx}}
								data-dsid={{columnHeader.dsId}}
							>
								<span class="bc__label">
									{{columnHeader.label}}
								</span>
								{{#if this.showPrev}}
									{{#if (eq idx this.firstVisibleColumnIndex)}}
										<a
											role="button"
											class="header-action header-action--prev"
											{{on "click" this.goPrev}}
										></a>
									{{/if}}
								{{/if}}
								{{#unless columnHeader.compareTitle}}
									{{#if this.showNext}}
										{{#if (eq idx this.lastVisibleColumnIndex)}}
											<a
												role="button"
												class="header-action header-action--next"
												{{on "click" this.goNext}}
											></a>
										{{/if}}
									{{/if}}
								{{/unless}}
							</th>
							{{#if columnHeader.compareTitle}}
								<th
									class="bc__table__cell--compare hideable col--{{idx}}
										action-target"
									data-colidx={{idx}}
									data-dsid={{columnHeader.dsId}}
								>
									<BlockContentTableCompareLabelComponent
										@text={{columnHeader.compareTitle}}
									/>
									{{#if this.showNext}}
										{{#if (eq idx this.lastVisibleColumnIndex)}}
											<a
												role="button"
												class="header-action header-action--next"
												{{on "click" this.goNext}}
											></a>
										{{/if}}
									{{/if}}
								</th>
							{{/if}}
						{{/each}}
					</tr>
				</thead>

				<CommonListLoadNextComponent
					@allItems={{this.allRowsForLoadNext}}
					@initialItemsShown={{this.initialItemsShown}}
					@nextItemsShown={{this.nextItemsShown}}
					@onVisibleItemsChanged={{this.updateVisibleItems}}
					as |_visibleItems loadNext|
				>
					<tbody {{didInsert (fn this.initializeLoadNext loadNext)}}>
						{{#each this.visibleRows as |row|}}
							<tr>
								{{#if this.showRowNumbers}}
									<td class="bc__table__cell--row-index">
										<span>{{row.sortedIndex}}</span>
									</td>
								{{/if}}
								{{#each this.labelColumnIndices as |i|}}
									<td
										class="bc__table__cell--label action-target"
										data-dsid={{row.dsId}}
									>
										<span class="bc__label">
											{{#if (eq i 0)}}
												{{#if this.mediaEnabled}}
													<BlockContentTableMediaLabelComponent
														@mediaUrl={{row.mediaUrl}}
														@label={{row.label.text}}
														@cells={{row.cells}}
														@isOther={{row.isOther}}
													/>
												{{/if}}
											{{/if}}
											<span class="bc__label-text">
												{{getLabelCellText row i}}
											</span>
										</span>
									</td>
								{{/each}}
								{{#each row.cells as |cell idx|}}
									<td
										class={{joinClasses
											(array
												"bc__table__cell--value"
												"hideable"
												(concat "col--" idx)
												"action-target"
												(if
													cell.isAggregation
													"bc__table__cell--aggregation-value"
												)
											)
										}}
										data-dsid={{cell.dsId}}
									>
										{{#if cell.isAggregation}}
											{{#if cell.type}}
												{{#if cell.label}}
													<span class="bc__label">
														{{cell.label}}
													</span>
												{{/if}}
												{{#each cell.points as |point|}}
													<div class="bc__number">
														<span class="bc__value">
															{{point.valueStr}}
														</span>
													</div>
												{{/each}}
											{{/if}}
										{{else}}
											{{#each cell.points as |point|}}
												<div class="bc__number" {{exposeDatapoint point}}>
													<BlockContentCommonValueComponent
														@model={{point}}
														@loading={{point.loading}}
													/>
												</div>
											{{else}}
												<div class="bc__number">
													<span class="bc__value">-</span>
												</div>
											{{/each}}
										{{/if}}
									</td>
									{{#if cell.hasCompare}}
										<td
											class="bc__table__cell--compare hideable col--{{idx}}
												action-target"
											data-dsid={{cell.dsId}}
										>
											{{#unless cell.isAggregation}}
												{{#each cell.points as |point|}}
													<div class="bc__number">
														<BlockContentCommonComparesComponent
															@model={{point.comparePoints}}
															@showTextInTooltip={{cell.showCompareTooltip}}
														/>
													</div>
												{{/each}}
											{{/unless}}
										</td>
									{{/if}}
								{{/each}}
							</tr>
						{{/each}}

						{{#if this.areAllItemsLoaded}}

							{{#if this.showAggregationAsRow}}
								<tr class="bc__table__row--aggregation">
									{{#if this.showRowNumbers}}
										<td class="bc__table__cell--row-index"></td>
									{{/if}}
									{{#each this.labelColumnIndices as |i|}}
										<td class="bc__table__cell--label">
											<span class="bc__label">
												{{#if (eq i 0)}}
													{{this.aggregationData.label}}
												{{/if}}
											</span>
										</td>
									{{/each}}
									{{#each this.aggregationData.cells as |aggregationCell idx|}}
										<td
											class="bc__table__cell--value bc__table__cell--aggregation-value hideable col--{{idx}}"
										>
											{{#if aggregationCell.type}}
												{{#if aggregationCell.label}}
													<span class="bc__label">
														{{aggregationCell.label}}
													</span>
												{{/if}}
												{{#each aggregationCell.points as |point|}}
													<div class="bc__number">
														<span class="bc__value">
															{{point.valueStr}}
														</span>
													</div>
												{{/each}}
											{{/if}}
										</td>
										{{#if aggregationCell.hasCompare}}
											<td
												class="bc__table__cell--compare hideable col--{{idx}}"
											></td>
										{{/if}}
									{{/each}}
								</tr>
							{{/if}}
						{{/if}}

					</tbody>

				</CommonListLoadNextComponent>

			</table>

		</div>
	</template>
}
